import React from 'react';
import {connect, ConnectedProps} from 'react-redux';
import LanguageSwitcher from './header/LanguageSwitcher';
import MiniProfile from './header/MiniProfile';
import DarkThemeSwitcher from '@src/components/DarkThemeSwitcher/DarkThemeSwitcher';
import {IReduxStoreState} from '@src/@interfaces';
import {toggleDesktopSidebarSize, toggleMobileSidebar} from '@src/_redux/theme/actions';
import classnames from '@src/helpers/classnames';
import HeaderSmartLoadingIndicator from './header/HeaderSmartLoadingIndicator';
import AsyncFilesUploadingProgressIndicator from './header/AsyncFilesUploadingProgressIndicator';
import app from '@src/helpers/app';
import {HttpEvents} from '@src/@types/services/Http/Http';
import {IEvent} from '@src/@interfaces/services/Events';
import config from '@src/helpers/config';
import HeaderLogo from './header/HeaderLogo';
import Translate from '@src/components/Translate/Translate';
import NotificationsToggler from './header/NotificationsToggler';
import Settings from '@src/components/_layouts/Default/components/settings/Settings';

type State = {
    currentServerAppVersion?: string,
    currentClientAppVersion?: string,
};

export class Header extends React.Component<ConnectedProps<typeof connector> & {
    wrapperRef?: React.RefObject<HTMLDivElement>,
}, State> {
    private readonly mainControlsRef: React.RefObject<HTMLDivElement>;

    state: State = {};

    constructor(props: any) {
        super(props);

        this.onRequestCompleted = this.onRequestCompleted.bind(this);
        this.toggleSidebar = this.toggleSidebar.bind(this);
        this.mainControlsRef = React.createRef<HTMLDivElement>();

        this.state = {
            currentClientAppVersion: config('client.app-version'),
        };
    }

    componentDidMount() {
        if (super.componentDidMount) {
            super.componentDidMount();
        }

        app('http').listen(HttpEvents.REQUEST_COMPLETED, this.onRequestCompleted);
    }

    componentWillUnmount() {
        if (super.componentWillUnmount) {
            super.componentWillUnmount();
        }

        app('events').forget(HttpEvents.REQUEST_COMPLETED, this.onRequestCompleted);
    }

    render() {
        return <header className={classnames('header', {
            'header--sidebar-collapsed': this.props.desktopSidebarSize === 'small',
        })}>
            <div className="header__menu-wrapper" ref={this.props.wrapperRef}>
                <div className="d-flex">
                    <div className="header__logo-block">
                        <HeaderLogo/>
                    </div>

                    <button
                            type="button"
                            className="header__menu-item btn btn-sm px-3 font-size-16"
                            onClick={this.toggleSidebar}
                    >
                        <i className="fa fa-fw fa-bars"/>
                    </button>
                </div>

                <div className="d-flex" ref={this.mainControlsRef}>
                    <DarkThemeSwitcher wrapperClassName="header__menu-item btn d-flex align-items-center"/>

                    <LanguageSwitcher toggleDropdownButtonClassName="header__menu-item"/>

                    <NotificationsToggler/>

                    <Settings>{({openPopup}) => <MiniProfile openSettingsPopup={openPopup}/>}</Settings>
                </div>

                <HeaderSmartLoadingIndicator/>

                <AsyncFilesUploadingProgressIndicator mainControlsContainerRef={this.mainControlsRef}/>

                {this.renderHasNewAppVersionNotification()}
            </div>
        </header>;
    }

    protected renderHasNewAppVersionNotification() {
        const {
            currentServerAppVersion: serverVer,
            currentClientAppVersion: clientVer,
        } = this.state;

        if (serverVer == null || clientVer == null || serverVer === clientVer) {
            return null;
        }

        return <div className="header__has-new-version-notification" data-count-height={true}>
            <Translate message="Header.your-app-is-out-of-date" tag="div"/>
        </div>;
    }

    protected toggleSidebar(e: React.MouseEvent) {
        e.stopPropagation();

        if (this.props.isMobileVersion) {
            this.props.toggleMobileSidebar();
        } else {
            this.props.toggleDesktopSidebarSize();
        }
    }

    protected onRequestCompleted(e: IEvent, {response}: { response: Response }) {
        if (response.headers.has('X-App-Version')) {
            this.setState({
                currentServerAppVersion: response.headers.get('X-App-Version') ?? undefined
            });
        }
    }
}

export const connector = connect((state: IReduxStoreState) => ({
    isMobileVersion: state.theme.isMobileVersion,
    desktopSidebarSize: state.theme.sidebar.desktop.size,
}), {
    toggleDesktopSidebarSize,
    toggleMobileSidebar,
});

export default connector(Header);
